import React, { FC, useMemo } from "react";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { exportDataToExcel } from "../../../utils/ExportData";
import { askTable, filtersText } from "../ask/reportAsk.utils";
import {
  DATE_PRESETS,
  formatMoney,
  inRange,
  percent,
  presetRange,
  revenueByMonth,
  revenueGroups,
  serviceGroups,
} from "../reports.utils";
import { useViewExtra } from "../saved/SavedViewContext";
import {
  DateMenu,
  PeopleClientTypeFilters,
  useReportFilters,
} from "../ui/ReportFilters";
import {
  Card,
  ColumnChart,
  Empty,
  Kpi,
  KpiGrid,
  LoadError,
  Loading,
  MiniBar,
  Note,
  NUM,
  ReportPage,
  Segmented,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllInvoices, useInvoiceLines } from "../useReportData";

type GroupBy = "client" | "service" | "person";
const GROUPS: { value: GroupBy; label: string }[] = [
  { value: "client", label: "Client" },
  { value: "service", label: "Service" },
  { value: "person", label: "Team member" },
];

const shortMoney = (n: number) =>
  n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : formatMoney(n);

/** The same dates one year earlier, for the "compared with last year" card. */
const yearBefore = (range: ReturnType<typeof presetRange>) => {
  if (!range) return null;
  const from = new Date(range.from);
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  const to = new Date(Math.min(range.to.getTime(), endOfToday.getTime()));
  from.setFullYear(from.getFullYear() - 1);
  to.setFullYear(to.getFullYear() - 1);
  return { from, to };
};

export const RevenueReport: FC = () => {
  const state = useReportFilters("thisYear");
  const { filters } = state;
  const [by, setBy] = useViewExtra<GroupBy>("groupBy", "client", GROUPS, "By");
  const range = useMemo(() => presetRange(filters.preset), [filters.preset]);
  const before = useMemo(() => yearBefore(range), [range]);
  const invoices = useAllInvoices({
    from: range?.from ?? null,
    to: range?.to ?? null,
    customerIds: filters.clientIds,
    userIds: filters.userIds,
  });
  const lastYear = useAllInvoices(
    {
      from: before?.from ?? null,
      to: before?.to ?? null,
      customerIds: filters.clientIds,
      userIds: filters.userIds,
    },
    !before,
  );
  const lines = useInvoiceLines(by !== "service");

  const invoiced = invoices.data.reduce((s, i) => s + (i.total || 0), 0);
  const unpaid = invoices.data.reduce(
    (s, i) => s + Math.max(0, i.balance || 0),
    0,
  );
  const lastYearTotal = lastYear.data.reduce((s, i) => s + (i.total || 0), 0);
  const change =
    lastYearTotal > 0
      ? Math.round(((invoiced - lastYearTotal) / lastYearTotal) * 100)
      : null;
  const thisMonth = presetRange("thisMonth");
  const monthTotal = invoices.data
    .filter((i) => inRange(i.invoiceDate, thisMonth))
    .reduce((s, i) => s + (i.total || 0), 0);
  const months = useMemo(
    () => revenueByMonth(invoices.data, range),
    [invoices.data, range],
  );

  const rows = useMemo(() => {
    if (by === "service") {
      return serviceGroups(invoices.data, lines.data).map((g) => ({
        ...g,
        paid: null as number | null,
        unpaid: null as number | null,
      }));
    }
    return revenueGroups(invoices.data, (i) =>
      by === "client"
        ? { id: i.customerId, name: i.customerName || "No client" }
        : {
            id: i.userId || "",
            name:
              `${i.userFirstName || ""} ${i.userLastName || ""}`.trim() ||
              "No assignee",
          },
    ).map((g) => ({
      ...g,
      paid: g.paid as number | null,
      unpaid: g.unpaid as number | null,
    }));
  }, [by, invoices.data, lines.data]);
  const rowsTotal = rows.reduce((s, r) => s + r.invoiced, 0);
  const isLoading = invoices.isLoading || (by === "service" && lines.isLoading);
  const v = (x: React.ReactNode) => (invoices.isLoading ? "…" : x);
  const firstCol = GROUPS.find((g) => g.value === by)?.label ?? "";

  const onExport = () =>
    exportDataToExcel(
      rows.map((r) => ({
        [firstCol]: r.name,
        Invoiced: Math.round(r.invoiced * 100) / 100,
        ...(r.paid !== null
          ? {
              Paid: Math.round(r.paid * 100) / 100,
              Unpaid: Math.round((r.unpaid || 0) * 100) / 100,
            }
          : {}),
        "Share %": percent(r.invoiced, rowsTotal),
      })),
      `Revenue by ${firstCol.toLowerCase()}`,
    );

  const tableName = `Revenue by ${firstCol.toLowerCase()}`;
  const shownRows = useFocusRows(tableName, rows, (r) => r.id || r.name);
  const { pageRows, pager } = usePagedRows(shownRows);

  const ask = useMemo(
    () => ({
      title: "Revenue",
      path: "/reports/revenue",
      description:
        "What was invoiced and what has been paid, month by month, by client, service or team member.",
      filters: filtersText([
        `Invoice date: ${
          DATE_PRESETS.find((p) => p.value === filters.preset)?.label
        }`,
        `Grouped by ${firstCol.toLowerCase()}`,
      ]),
      kpis: [
        {
          label: "Invoiced",
          value: `${formatMoney(invoiced)} (${invoices.data.length} invoices)`,
        },
        {
          label: "Paid",
          value: `${formatMoney(invoiced - unpaid)} (${percent(
            invoiced - unpaid,
            invoiced,
          )}% of invoiced)`,
        },
        { label: "Invoiced this month", value: formatMoney(monthTotal) },
        ...(before
          ? [
              {
                label: "Compared with last year (same dates a year earlier)",
                value:
                  change === null ? "—" : `${change > 0 ? "+" : ""}${change}%`,
              },
            ]
          : []),
      ],
      tables: [
        askTable(
          "Invoiced per month",
          months,
          [
            {
              key: "month",
              label: "Month",
              kind: "text",
              value: (m) => m.label,
            },
            {
              key: "invoiced",
              label: "Invoiced",
              kind: "money",
              value: (m) => m.total,
            },
          ],
          (m) => m.key,
        ),
        askTable(
          tableName,
          rows,
          [
            {
              key: "name",
              label: firstCol,
              kind: "text",
              value: (r) => r.name,
            },
            {
              key: "invoiced",
              label: "Invoiced",
              kind: "money",
              value: (r) => r.invoiced,
            },
            ...(by !== "service"
              ? [
                  {
                    key: "paid",
                    label: "Paid",
                    kind: "money" as const,
                    value: (r: typeof rows[number]) => r.paid || 0,
                  },
                  {
                    key: "unpaid",
                    label: "Unpaid",
                    kind: "money" as const,
                    value: (r: typeof rows[number]) => r.unpaid || 0,
                  },
                ]
              : []),
            {
              key: "share",
              label: "Share of revenue",
              kind: "percent",
              value: (r) => percent(r.invoiced, rowsTotal),
            },
          ],
          (r) => r.id || r.name,
        ),
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, months, invoices.data, lastYear.data, by, filters.preset, before],
  );

  return (
    <ReportPage
      title={"Revenue"}
      description={
        "What you invoiced and what has been paid, month by month, by client, service or team member."
      }
      onExport={rows.length ? onExport : undefined}
      ask={isLoading || lastYear.isLoading ? undefined : ask}
      askSuggestions={[
        "Which clients brought in the most revenue?",
        "Which month was our best?",
        "Who has the largest unpaid balance?",
      ]}
      filters={
        <>
          <DateMenu
            value={filters.preset}
            onChange={(preset) => state.setFilters({ preset })}
            label={"Invoice date"}
          />
          <PeopleClientTypeFilters
            state={state}
            showWorkTypes={false}
            peopleLabel={"Assignee"}
          />
        </>
      }>
      {(invoices.isError || lines.isError) && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Invoiced"}
          value={v(formatMoney(invoiced))}
          note={`${invoices.data.length} invoices`}
        />
        <Kpi
          label={"Paid"}
          value={v(formatMoney(invoiced - unpaid))}
          note={`${percent(invoiced - unpaid, invoiced)}% of invoiced`}
        />
        <Kpi
          label={"This month"}
          value={v(formatMoney(monthTotal))}
          note={"Invoiced this month"}
        />
        {before && (
          <Kpi
            label={"Compared with last year"}
            value={
              lastYear.isLoading || invoices.isLoading
                ? "…"
                : change === null
                ? "—"
                : `${change > 0 ? "+" : ""}${change}%`
            }
            note={"Same dates a year earlier"}
            tone={change !== null && change < 0 ? "red" : "green"}
          />
        )}
      </KpiGrid>

      <Card title={"Invoiced per month"}>
        {invoices.isLoading ? (
          <Loading />
        ) : (
          <ColumnChart
            items={months.map((m) => ({
              key: m.key,
              label: m.label,
              value: m.total,
              top: shortMoney(m.total),
            }))}
          />
        )}
      </Card>

      <Card
        title={"Revenue by"}
        right={
          <Segmented
            label={"Group by"}
            value={by}
            onChange={setBy}
            options={GROUPS}
          />
        }>
        {isLoading ? (
          <Loading />
        ) : !rows.length ? (
          <Empty text={"No invoices in this period."} />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  {firstCol}
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Invoiced
                </th>
                {by !== "service" && (
                  <th scope={"col"} className={`${TH} ${NUM}`}>
                    Paid
                  </th>
                )}
                {by !== "service" && (
                  <th scope={"col"} className={`${TH} ${NUM}`}>
                    Unpaid
                  </th>
                )}
                <th scope={"col"} className={TH}>
                  Share of revenue
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => {
                const pct = percent(r.invoiced, rowsTotal);
                return (
                  <tr key={r.id || r.name}>
                    <td className={`${TD} font-bold`}>{r.name}</td>
                    <td className={`${TD} ${NUM} font-extrabold`}>
                      {formatMoney(r.invoiced)}
                    </td>
                    {by !== "service" && (
                      <td className={`${TD} ${NUM}`}>
                        {formatMoney(r.paid || 0)}
                      </td>
                    )}
                    {by !== "service" && (
                      <td className={`${TD} ${NUM}`}>
                        {r.unpaid ? formatMoney(r.unpaid) : "—"}
                      </td>
                    )}
                    <td className={TD}>
                      <span className={"inline-block w-10 font-bold"}>
                        {pct}%
                      </span>
                      <MiniBar pct={pct} color={"#6B4EFF"} width={140} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableBox>
        )}
      </Card>

      {by === "service" && (
        <Note>
          Each invoice&apos;s total is shared between its services by line
          amount. Invoices with no lines show as &quot;Not split into
          services&quot;.
        </Note>
      )}
    </ReportPage>
  );
};
